import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { CourseBuilder } from '../types';
import { BlockContainer } from '../BlockContainer';
import { formGroup, formControl, FormDataType, formArray } from '@core/utils/form';
import classNames from 'classnames';
import { defineBlockAdvanceForm } from '../Common/AdvanceSettings';
import { useNonNilObservable, useObservable } from '@core/utils/hooks/rxjs';
import { useConfigureBlock, useCourseForm } from '@modules/course/CourseBuilder/hooks';
import { TodoListBlockSettings } from './TodoListBlockSettings';
import './index.scss';
import { Col, Container, Row } from 'reactstrap';
import { useDidMount } from '@core/utils/hooks/react';
import { HTMLService } from '@business/services';
import { FormCheckbox } from '@core/components/form';
import { Button } from '@core/components/Button';
import { R } from '@core/utils/r';
import { TodoListBlockBloc } from './TodoListBlockBloc';
import { useBloc } from '@core/utils/bloc';
import { tap } from 'rxjs/operators';
import { of } from 'rxjs';

export const TODOLIST_BLOCK_AI_INSTRUCTION = `
TODO LIST BLOCK - AI Instruction Format for Widget Variation

The "todoList" block allows creating checklists grouped into sections. Users must mark all tasks complete to proceed.

Supported Variation:
- "todoList" (only variation for this block)

Expected JSON Structure:
- type: "todoList"
- variation: "todoList"
- data: {
    name: string,               // title of the todo list
    instructions: string,       // HTML or plain text instructions for completing the checklist
    sections: [
      {
        sectionTitle: string,
        items: [
          {
            item: string,
            isTodoCompleted: boolean
          }
        ]
      }
    ],
  }

Example JSON:
{
  "type": "todoList",
  "variation": "todoList",
  "data": {
    "name": "Onboarding Tasks",
    "instructions": "<div>Please complete the following onboarding tasks before your first day.</div>",
    "sections": [
      {
        "sectionTitle": "HR Tasks",
        "items": [
          { "item": "Complete tax forms", "isTodoCompleted": false },
          { "item": "Upload ID documents", "isTodoCompleted": false }
        ]
      },
      {
        "sectionTitle": "Setup Tasks",
        "items": [
          { "item": "Install company software", "isTodoCompleted": false },
          { "item": "Configure email", "isTodoCompleted": false }
        ]
      }
    ],
  }
}
`;

function defineTodoListSectionItemForm() {
  return formGroup({
    item: formControl<string>({
      placeholder: 'Item',
      initialValue: '',
    }),
    isTodoCompleted: formControl<boolean>({
      initialValue: false,
    }),
  });
}

function defineTodoListSectionForm() {
  return formGroup({
    sectionTitle: formControl<string>({
      placeholder: 'sectionTitle',
      initialValue: '',
    }),
    items: formArray(() => defineTodoListSectionItemForm()),
  });
}

function defineTodoListBlockForm() {
  return formGroup({
    name: formControl<string>({
      initialValue: 'Name',
    }),
    instructions: formControl<string>({
      initialValue: 'Instructions',
    }),
    sections: formArray(() => defineTodoListSectionForm()),
    advance: defineBlockAdvanceForm(),
  });
}

export type TodoListFormData = FormDataType<typeof defineTodoListBlockForm>;
export type TodoListForm = ReturnType<typeof defineTodoListBlockForm>;

export type TodoListSectionForm = ReturnType<typeof defineTodoListSectionForm>;
export type TodoListSectionFormData = FormDataType<typeof defineTodoListSectionForm>;

export type TodoListSectionItemForm = ReturnType<typeof defineTodoListSectionItemForm>;
export type TodoListSectionItemFormData = FormDataType<typeof defineTodoListSectionItemForm>;

export enum TodoListBlockVariation {
  todoList = 'todoList',
}

export const TodoListBlock = memo((props: CourseBuilder.BlockProps<TodoListBlockVariation>) => {
  const { variation, lessonId, preview, editable } = props;
  const form = useMemo(() => defineTodoListBlockForm(), []);
  const formValue = useNonNilObservable(form.value$);
  const [isCompleted, setIsCompleted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const sections = useObservable(form.controls.sections.items$, []);
  const bloc = useBloc(TodoListBlockBloc, lessonId);

  useConfigureBlock({
    props,
    form,
    contentComponent: TodoListBlockSettings,
    contentProps: { form, variation },
  });

  useDidMount(() => {
    if (props.preview && !formValue.sections.length) {
      form.patchValue({
        name: 'Name',
        instructions: 'Instruction',
        sections: [
          {
            sectionTitle: 'Section',
            items: [
              {
                item: 'Item 1',
                isTodoCompleted: false,
              },
            ],
          },
        ],
      });
    }
  });

  const onComplete = useCallback(() => {
    let check = true;
    formValue.sections.forEach(s => {
      s.items.forEach(i => {
        if (!i.isTodoCompleted) {
          setErrorMsg('');
          check = false;
          return;
        }
      });

      if (!check) {
        return setErrorMsg('Please check all the tasks to complete.');
      }
    });

    if (props.editable || props.preview || R.isEmpty(lessonId)) return setIsCompleted(check);

    bloc
      .onProgress(lessonId || '', {
        title: formValue.name,
        answer: '',
        correct: '',
        status: 'pass',
      })
      .pipe(tap(() => setIsCompleted(check)))
      .subscribe();
  }, [lessonId, props.preview, props.editable, bloc, formValue]);

  const onResetDrill = useCallback(() => {
    form.patchValue({ sections: formValue.sections.map(s => ({ ...s, isTodoCompleted: false })) });
    setIsCompleted(false);
    setErrorMsg('');
  }, [form, formValue.sections]);

  const onSubmit = useCallback(() => of(isCompleted), [isCompleted]);

  useCourseForm(props.id, form, { onSubmit });

  return (
    <Container>
      <BlockContainer className={classNames('d-flex flex-fill', 'block-embed', 'mt-3', variation)} advanceForm={formValue.advance}>
        {!isCompleted && (
          <>
            <Row>
              <Col md={12}>
                <h3>{formValue.name}</h3>
              </Col>
            </Row>
            <Row className="ml-3">
              <Col md={12}>
                <p>{HTMLService.toReact(formValue.instructions)}</p>
              </Col>
            </Row>
            {sections.map((section, index) => {
              return <TodoListSection section={section} index={index} i={index} />;
            })}
            <div className="d-flex align-items-end mt-2 flex-column">
              <span className="text-danger mb-2">{errorMsg}</span>
              <Button type="button" onClick={onComplete}>
                Next
              </Button>
            </div>
          </>
        )}
        {isCompleted && (
          <div className="d-flex align-items-start mt-2 flex-column">
            <span className="text-success mb-2">Congratulations! You have completed this drill successfully.</span>
            <div className="d-flex align-items-end mt-2 flex-column">
              <Button type="button" onClick={onResetDrill}>
                Redo
              </Button>
            </div>
          </div>
        )}
      </BlockContainer>
    </Container>
  );
});

interface TodoListSectionProps {
  i: number;
  section: TodoListSectionForm;
  index: number;
}

const TodoListSection = memo((props: TodoListSectionProps) => {
  const { section, i, index } = props;
  const sectionItems = useObservable(section.controls.items.items$, []);
  const sectionValue = useObservable(section.controls.sectionTitle.value$);

  return (
    <Row key={`todo-list-section-${index}`} className="ml-3">
      <Col md={12} className="mb-2">
        <h3>{sectionValue}</h3>
      </Col>
      <Col md={12}>
        {sectionItems.map((item, index) => {
          return <TodoListSectionItem item={item} index={index} key={index} />;
        })}
      </Col>
    </Row>
  );
});

interface TodoListSectionItemProps {
  key: number;
  item: TodoListSectionItemForm;
  index: number;
}

const TodoListSectionItem = memo((props: TodoListSectionItemProps) => {
  const { item, key, index } = props;
  const sectionItem = useObservable(item.controls.item.value$);

  return (
    <div key={`todo-list-section-item-${index}`} className="todo-list-item d-flex ml-2">
      <FormCheckbox control={item.controls.isTodoCompleted} className="todo-list-checkbox" noPadding={true} />
      {sectionItem}
    </div>
  );
});
